import { useTranslation } from '@jq4race/i18n';
import { Button, Card, CardContent } from '@jq4race/ui';
import { useLiveQuery } from 'dexie-react-hooks';
import { AlertTriangle, BadgeCheck, CheckCircle2, Loader2, MessageSquare } from 'lucide-react';

import { isBlockedBySubscription } from '../../../offline/domain/classify-sync-error';
import type { OutboxItem } from '../../../offline/domain/outbox-item';
import { offlineDb } from '../../../offline/infrastructure/dexie/offline-db';
import { PageHeader } from '../../../shell/ui/PageHeader';

interface CheckinSuccessScreenProps {
  /** The outbox item's own id (= clientRequestId) — the live query key. */
  outboxId: string;
  /** Shown immediately, before the Dexie row is even readable (design D16). */
  provisionalCode: string;
  plate: string;
  onNewCheckin: () => void;
}

/**
 * resolveSuccessDisplayState — pure projection of an outbox row onto the
 * states this screen actually renders differently (task 6.7). `blocked`
 * (subscription suspended/canceled) is split out of `syncing`: the item is
 * still queued, but waiting will not fix it, so the valet must be told to
 * contact their administrator instead of seeing an endless spinner. Extracted so
 * the branching itself is testable with zero Dexie/React (Extract-Before-
 * Mock). `undefined` covers the brief instant before `useLiveQuery`'s first
 * read resolves — treated the same as pending/syncing (still provisional).
 */
export function resolveSuccessDisplayState(
  item: OutboxItem | undefined,
): 'syncing' | 'blocked' | 'synced' | 'needs-review' {
  if (item && isBlockedBySubscription(item)) return 'blocked';
  if (!item || item.state === 'pending' || item.state === 'syncing') return 'syncing';
  if (item.state === 'synced') return 'synced';
  // rejected | needs-auth | held | discarded — Slice 7 owns the detailed,
  // per-reason UX (RejectedItemSheet); this screen only ever needs to point
  // the valet at /pending to resolve it.
  return 'needs-review';
}

export function CheckinSuccessScreen({
  outboxId,
  provisionalCode,
  plate,
  onNewCheckin,
}: CheckinSuccessScreenProps) {
  const { t } = useTranslation('checkin');

  // pwa-offline-first Slice 6 (task 6.7) — reacts live to the Dexie row:
  // today nothing ever writes past `pending` (the SyncEngine is Slice 7), so
  // this always renders "syncing" in practice — that IS the correct,
  // designed behavior for this slice (binding requirement: immediate
  // confirmation with a syncing state, official number arrives at sync).
  const item = useLiveQuery(
    () => offlineDb.outbox.get(outboxId) as unknown as Promise<OutboxItem | undefined>,
    [outboxId],
  );
  const displayState = resolveSuccessDisplayState(item);
  const result = item?.result ?? null;

  return (
    <div className="space-y-6">
      {/* Success icon + header */}
      <div className="flex flex-col items-center gap-4 pt-4">
        <div className="rounded-full bg-accent/10 p-4 text-jq-gold-bright ring-1 ring-accent/30">
          <CheckCircle2 className="size-8" strokeWidth={1.5} />
        </div>
        <PageHeader eyebrow={t('checkinSuccess.eyebrow')} title={t('checkinSuccess.title')} />
      </div>

      {/* Ticket details */}
      <Card className="border-border bg-card">
        <CardContent className="space-y-4 px-5 py-5">
          <div className="flex items-center justify-between">
            <span className="jq-eyebrow text-muted-foreground">
              {displayState === 'synced'
                ? t('checkinSuccess.ticketLabel')
                : t('checkinSuccess.provisionalLabel')}
            </span>
            <span className="jq-data tabular-nums text-jq-gold-bright">
              {displayState === 'synced' && result ? `#${result.ticketNumber}` : provisionalCode}
            </span>
          </div>

          <div className="flex items-center justify-between border-t border-border pt-4">
            <span className="jq-eyebrow text-muted-foreground">
              {t('checkinSuccess.plateLabel')}
            </span>
            <span className="jq-mono jq-h5 text-foreground">{plate}</span>
          </div>

          {displayState === 'syncing' && (
            <div
              role="status"
              className="flex items-center gap-2 rounded-sm border border-accent/30 bg-accent/10 px-3 py-2.5"
            >
              <Loader2
                className="size-4 shrink-0 animate-spin text-jq-gold-bright"
                strokeWidth={1.5}
              />
              <span className="jq-body-sm text-foreground">
                <span className="font-semibold">{t('checkinSuccess.syncingLabel')}</span>{' '}
                {t('checkinSuccess.syncingDetail')}
              </span>
            </div>
          )}

          {displayState === 'blocked' && (
            <div
              role="alert"
              className="flex items-center gap-2 rounded-sm border border-destructive/60 bg-destructive/10 px-3 py-2.5"
            >
              <AlertTriangle className="size-4 shrink-0 text-destructive" strokeWidth={1.5} />
              <span className="jq-body-sm text-foreground">
                {t('checkinSuccess.subscriptionBlocked')}
              </span>
            </div>
          )}

          {displayState === 'needs-review' && (
            <div
              role="alert"
              className="flex items-center gap-2 rounded-sm border border-destructive/60 bg-destructive/10 px-3 py-2.5"
            >
              <AlertTriangle className="size-4 shrink-0 text-destructive" strokeWidth={1.5} />
              <span className="jq-body-sm text-foreground">{t('checkinSuccess.needsReview')}</span>
            </div>
          )}

          {displayState === 'synced' && result?.coveredByMembership && (
            <div
              role="status"
              className="flex items-center gap-2 rounded-sm border border-emerald-600/60 bg-emerald-600/10 px-3 py-2.5"
            >
              <BadgeCheck className="size-4 shrink-0 text-emerald-600" strokeWidth={1.5} />
              <span className="jq-body-sm text-foreground">
                <span className="font-semibold">{t('checkinSuccess.memberLabel')}</span>{' '}
                {t('checkinSuccess.memberDetail')}
              </span>
            </div>
          )}

          {displayState === 'synced' && result?.smsDispatched && (
            <div className="flex items-center gap-2 rounded-sm border border-accent/30 bg-accent/10 px-3 py-2.5">
              <MessageSquare className="size-4 shrink-0 text-jq-gold-bright" strokeWidth={1.5} />
              <span className="jq-body-sm text-foreground">{t('checkinSuccess.smsSent')}</span>
            </div>
          )}

          {/* fourth adversarial review, HIGH #1 — the ticket synced fine,
              but its incident photos were DROPPED. Never silently lose that
              information — surface it right where the operator already is.
              Fifth adversarial review, finding 1 — a `reject`-classified
              drop is NOT always `feature_not_in_plan` (e.g. a 400/413/422
              from the upload itself); claiming "plan doesn't include damage
              reports" for those would be an outright lie about the actual
              cause. Show the plan-specific copy ONLY for that one code, and
              a generic message naming the real reason otherwise. */}
          {/* pwa-offline-first Slice 7 — deferred finding from Slice 6
              round 5 (LOW #6): the server may have CLAMPED the claimed
              capture time (design D15 / decision 18's hard cap) — flag it
              for the operator right here, with the OFFICIAL checked-in
              time the server actually used. Also shown DURABLY on
              /pending (hasDurableWarning) since this screen is transient. */}
          {displayState === 'synced' && result?.checkinTimeSource === 'offline_capped' && (
            <div
              role="status"
              className="flex items-center gap-2 rounded-sm border border-amber-600/60 bg-amber-600/10 px-3 py-2.5"
            >
              <AlertTriangle className="size-4 shrink-0 text-amber-600" strokeWidth={1.5} />
              <span className="jq-body-sm text-foreground">
                {t('checkinSuccess.checkinTimeAdjusted', {
                  time: result.checkedInAt ? new Date(result.checkedInAt).toLocaleTimeString() : '',
                })}
              </span>
            </div>
          )}

          {displayState === 'synced' && result?.incidentPhotosDropped && (
            <div
              role="status"
              className="flex items-center gap-2 rounded-sm border border-amber-600/60 bg-amber-600/10 px-3 py-2.5"
            >
              <AlertTriangle className="size-4 shrink-0 text-amber-600" strokeWidth={1.5} />
              <span className="jq-body-sm text-foreground">
                {result.incidentPhotosDropped.code === 'feature_not_in_plan'
                  ? t('checkinSuccess.incidentPhotosDroppedPlan')
                  : t('checkinSuccess.incidentPhotosDroppedGeneric', {
                      reason: result.incidentPhotosDropped.code,
                    })}
              </span>
            </div>
          )}
        </CardContent>
      </Card>

      {/* New check-in action — always available regardless of sync state:
          the valet must be able to keep working the curb without waiting on
          a sync that may take a while. */}
      <Button className="h-14 w-full text-[15px]" onClick={onNewCheckin}>
        {t('checkinSuccess.newCheckin')}
      </Button>
    </div>
  );
}
